import axios from 'axios';
import { queryClient } from '#client/utils/queryClient.js';

const API_URL = import.meta.env.VITE_API_URL;

// Transfer Query Options
const transferQueryOptions = {
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
};

const getTransfersQueryOptions = (filters = {}, sortBy = 'id', sortDirection = 'desc', page = 1, itemsPerPage = 20) => ({
    queryKey: ['transfers', { page, itemsPerPage, filters, sortBy, sortDirection }],
    ...transferQueryOptions,
});

const getTransferQueryOptions = (id) => ({
    queryKey: ['transfer', id],
    enabled: !!id,
    ...transferQueryOptions,
});

export const inventoryTransferService = {
    /**
     * Builds URL parameters for API requests
     */
    buildApiParams: (page, itemsPerPage, filters = {}) => {
        const params = new URLSearchParams({
            skip: String((page - 1) * itemsPerPage),
            take: String(itemsPerPage),
        });

        // Add search and filter parameters
        if (filters.queryValue) params.set('search', filters.queryValue);
        if (Array.isArray(filters.statusFilter) && filters.statusFilter.length > 0) params.set('status', filters.statusFilter.join(','));
        if (filters.typeFilter) params.set('type', filters.typeFilter);
        if (filters.toLocationFilter) params.set('inLocationId', filters.toLocationFilter);
        if (filters.fromLocationFilter) params.set('outLocationId', filters.fromLocationFilter);
        if (filters.globalLocationFilter) params.set('globalLocationId', filters.globalLocationFilter);
        if (filters.createdDateStartFilter) params.set('createdDateStart', filters.createdDateStartFilter);
        if (filters.createdDateEndFilter) params.set('createdDateEnd', filters.createdDateEndFilter);
        if (filters.updatedDateStartFilter) params.set('updatedDateStart', filters.updatedDateStartFilter);
        if (filters.updatedDateEndFilter) params.set('updatedDateEnd', filters.updatedDateEndFilter);
        if (filters.shippedDateStartFilter) params.set('shippedDateStart', filters.shippedDateStartFilter);
        if (filters.shippedDateEndFilter) params.set('shippedDateEnd', filters.shippedDateEndFilter);

        // Add sorting parameters
        if (filters.sortColumn !== undefined && filters.sortDirection) {
            params.set('sortColumn', String(filters.sortColumn));
            params.set('sortDirection', filters.sortDirection);
        }

        return params;
    },

    /**
     * Fetches paginated purchase orders from API using react-query
     */
    async fetchPaginatedTransfers(page = 1, itemsPerPage = 20, filters = {}) {
        const response = await queryClient.fetchQuery({
            ...getTransfersQueryOptions(filters, filters.sortBy, filters.sortDirection, page, itemsPerPage),
            queryFn: async () => {
                const params = this.buildApiParams(page, itemsPerPage, filters);
                const response = await axios.get(`${API_URL}/api/app/inventory-transfers`, { params });

                const wrapper = response.data?.data ?? {};
                const items = Array.isArray(wrapper.data) ? wrapper.data : [];
                const total = typeof wrapper.total === 'number' ? wrapper.total : items.length;

                return {
                    rows: items,
                    total,
                };
            },
        });
        return response;
    },

    /**
     * Exports matching short lines across all pages.
     */
    async exportTransferExceptions(filters = {}) {
        const params = this.buildApiParams(1, 100, filters);
        params.delete('skip');
        params.delete('take');
        return axios.get(`${API_URL}/api/app/inventory-transfers/export/exceptions`, { params });
    },

    /**
     * Fetches a single transfer by ID using react-query
     */
    async fetchTransfer(id) {
        const response = await queryClient.fetchQuery({
            ...getTransferQueryOptions(id),
            queryFn: async () => {
                const response = await axios.get(`${API_URL}/api/app/inventory-transfers/${id}`);
                return response.data;
            },
        });

        return response;
    },

    async fetchTransferByName(name) {
        const response = await queryClient.fetchQuery({
            ...getTransferQueryOptions(name),
            queryFn: async () => {
                const response = await axios.get(`${API_URL}/api/app/inventory-transfers/lookup-by-name/${name}`);
                return response.data;
            },
        });

        return response;
    },

    /**
     * Fetches inventory by variant by store location using react-query
     */
    async fetchInventoryByStoreLocation(variantId, locationId) {
        const updateData = {
            variantId,
            locationId,
        };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/inventory`, updateData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return response.data;
    },

    /**
     * Fetches inventory by variant by store location using react-query
     */
    async fetchInventoryByStoreLocationAndSku(sku, locationId) {
        const updateData = {
            sku,
            locationId,
        };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/inventory`, updateData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return response.data;
    },

    /**
     * Fetches bulk inventory by variant ids and location id
     */
    async fetchBulkInventoryByVariantIds(variantIds, locationId) {
        const updateData = {
            variantIds,
            locationId,
        };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/inventory/bulk`, updateData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return response.data;
    },

    /**
     * Updates a transfer
     */
    async updateTransfer(id, updateData, originalTrasnsferData, logsToCreate = []) {
        //because we're updating inventory on Initiated, if another save happens checking status is not enough, pass this through to ensure we're only updating inventory on the first Initiated save.
        const firstInitiated = updateData.status == 'Initiated' && originalTrasnsferData.status != 'Initiated';

        let action = 'UPDATE';
        let message = `${action}D Store to Store Transfer`;
        if (updateData.status != originalTrasnsferData.status) {
            message += ` status to ${updateData.status}.`;
        }

        if (updateData.status == 'Complete') {
            action = 'SUBMIT';
            message = `COMPLETED Store to Store Transfer`;
        }

        if (updateData.status == 'Canceled') {
            action = 'CANCELED';
            message = `CANCELED Store to Store Transfer`;
        }

        const logdata = {
            staffId: updateData.currentUserId,
            objectType: 'InventoryTransfer',
            objectId: updateData.displayTransferId,
            action: action,
            message: message,
            timestamp: new Date(),
        };

        logsToCreate.push(logdata);

        // pass logsToCreate in the request body - server will handle logging
        const requestData = {
            ...updateData,
            logsToCreate,
        };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/${id}/update?firstInitiated=${firstInitiated}`, requestData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Invalidate related queries after update
        queryClient.invalidateQueries({ queryKey: ['transfer', id] });
        queryClient.invalidateQueries({ queryKey: ['transfers'] });
        queryClient.invalidateQueries({ queryKey: ['activity-logs', 'InventoryTransfer', updateData.displayTransferId] });

        return response.data;
    },

    async linkOverageTranfers(newTransfer, originalTransfer, currentUserId) {
        const logdata = {
            staffId: currentUserId,
            objectType: 'InventoryTransfer',
            objectId: newTransfer.displayTransferId,
            action: 'LINK',
            message: `CREATED Store to Store Transfer ${newTransfer.displayTransferId} created due to overage from ${originalTransfer.displayTransferId}.`,
            timestamp: new Date(),
        };

        //create linking log message for newTransfer
        await axios.post(`${API_URL}/app/activity-logs/create`, logdata, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        logdata.objectId = originalTransfer.displayTransferId;

        //create linking log message for originalTransfer
        await axios.post(`${API_URL}/app/activity-logs/create`, logdata, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        queryClient.invalidateQueries({ queryKey: ['activity-logs', 'InventoryTransfer', originalTransfer.displayTransferId] });
        queryClient.invalidateQueries({ queryKey: ['activity-logs', 'InventoryTransfer', newTransfer.displayTransferId] });
    },

    async createTransfer(data, logsToCreate = []) {
        // pass logsToCreate in the request body - server will handle logging
        const requestData = {
            ...data,
            logsToCreate,
        };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/create`, requestData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Invalidate related queries after update
        queryClient.invalidateQueries({ queryKey: ['transfers'] });

        const displayTransferId = response.data.data.transfer.displayTransferId;

        queryClient.invalidateQueries({ queryKey: ['activity-logs', 'InventoryTransfer', displayTransferId] });

        return response.data;
    },
    async checkProduct(sku, barcode) {
        const data = { sku, barcode };

        const response = await axios.post(`${API_URL}/api/app/inventory-transfers/check-product/`, data);

        return response.data;
    },
};

// Export query options for use in components that need direct access
export { getTransfersQueryOptions, getTransferQueryOptions };
